<template>
<v-dialog
  :value="modalVisible"
  persistent
  max-width="800"
  scrollable
>
  <v-card class="create-card-modal">
    <!-- Заголовок -->
    <v-card-title class="headline primary white--text">
      <v-icon left color="white">mdi-card-plus</v-icon>
      Создание карточки услуги

      <v-spacer></v-spacer>

      <!-- Кнопка закрытия (только если не в процессе) -->
      <v-btn
        icon
        dark
        :disabled="isCreating"
        @click="closeModal"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Контент модалки -->
    <v-card-text class="pa-4">
      <!-- Прогресс-бар -->
      <div class="mb-6">
        <div class="d-flex justify-space-between mb-2">
            <span class="text-caption">
              Общий прогресс: {{ overallProgress }}%
            </span>
          <span
            class="text-caption font-weight-medium"
            :class="statusColorClass"
          >
              {{ currentStatusText }}
            </span>
        </div>
        <v-progress-linear
          :value="overallProgress"
          height="10"
          color="primary"
          rounded
        ></v-progress-linear>
      </div>

      <!-- Список шагов -->
      <v-list dense class="steps-list">
        <v-list-item
          v-for="step in stepsCreating"
          :key="step.id"
          class="mb-2 step-item d-flex flex-column align-start justify-center"
          :class="getStepClass(step)"
        >
          <div class="d-flex gap-2">
            <!-- Номер шага с иконкой статуса -->
            <v-list-item-avatar class="step-avatar">
              <v-icon
                v-if="step.status === 'finished'"
                color="success"
                class="step-icon"
              >
                mdi-check-circle
              </v-icon>
              <v-icon
                v-else-if="step.status === 'error'"
                color="error"
                class="step-icon"
              >
                mdi-alert-circle
              </v-icon>
              <v-icon
                v-else-if="step.status === 'started'"
                color="primary"
                class="step-icon"
              >
                mdi-loading mdi-spin
              </v-icon>
              <v-badge
                v-else
                color="grey lighten-2"
                :content="step.id + 1"
                overlap
              >
                <v-icon color="grey" class="step-icon">
                  mdi-circle-outline
                </v-icon>
              </v-badge>
            </v-list-item-avatar>

            <!-- Контент шага -->
            <v-list-item-content>
              <v-list-item-title class="step-title">
                {{ step.text }}
                <!-- Длительность выполнения (если завершен) -->
                <span
                  v-if="step.duration"
                  class="text-caption ml-2 grey--text"
                >
                  ({{ step.duration }}с)
                </span>
              </v-list-item-title>

              <!-- Описание ошибки -->
              <v-list-item-subtitle
                v-if="step.status === 'error' && step.detailError.length"
                class="error--text mt-1"
              >
                <v-icon small color="error" class="mr-1">mdi-alert</v-icon>
                {{ step.detailError[0] }}

                <!-- Подробности ошибки (раскрывающийся список) -->
                <v-btn
                  v-if="step.detailError.length > 1"
                  x-small
                  text
                  color="error"
                  @click="toggleErrorDetails(step.id)"
                  class="ml-2"
                >
                  {{ showErrorDetails[step.id] ? 'Скрыть' : 'Подробнее' }}
                </v-btn>

                <!-- Дополнительные ошибки -->
                <v-expand-transition>
                  <div v-if="showErrorDetails[step.id]">
                    <div
                      v-for="(error, idx) in step.detailError.slice(1)"
                      :key="idx"
                      class="ml-4 mt-1 text-caption"
                    >
                      • {{ error }}
                    </div>
                  </div>
                </v-expand-transition>
              </v-list-item-subtitle>

              <!-- Время начала/завершения -->
              <v-list-item-subtitle
                v-if="step.startTime || step.endTime"
                class="text-caption grey--text mt-1"
              >
                <template v-if="step.startTime">
                  Начало: {{ formatTime(step.startTime) }}
                </template>
                <template v-if="step.endTime">
                  <span v-if="step.startTime"> • </span>
                  Завершение: {{ formatTime(step.endTime) }}
                </template>
              </v-list-item-subtitle>
            </v-list-item-content>
          </div>
          <div v-if="step.modules">
            <div class="d-flex flex-column ml-6">
              <span v-for="(stepModule,idx) of step.modules" :key="idx" class="d-flex flex-column">
                <div class="d-flex mb-3" v-if="stepModule.availability">
                  <div>
                    <v-icon
                      v-if="stepModule.status === 'finished'"
                      color="success"
                      class="step-icon"
                    >
                  mdi-check-circle
                </v-icon>
                    <v-icon
                      v-else-if="stepModule.status === 'error'"
                      color="error"
                      class="step-icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <v-icon
                      v-else-if="stepModule.status === 'started'"
                      color="primary"
                      class="step-icon"
                    >
                      mdi-loading mdi-spin
                    </v-icon>
                    <v-badge
                      v-else
                      color="grey lighten-2"
                      :content="stepModule.id + 1"
                      overlap
                    >
                    <v-icon color="grey" class="step-icon">
                      mdi-circle-outline
                    </v-icon>
                  </v-badge>
                  </div>
                  <div class="ml-5">
                    {{stepModule.text}}
                  </div>
                </div>
                <div class="d-flex mb-3" v-if="stepModule.error.length">
                  <div class="ml-5">
                    <div v-for="(errorModule,id) of stepModule.error" :key="id">
                      <span class="text-error d-flex">
                        <v-icon small color="error">
                          mdi-alert-circle
                        </v-icon>
                        <span class="ml-2">
                          {{errorModule}}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </span>
            </div>
          </div>

        </v-list-item>
      </v-list>

      <!-- Сводная информация (после завершения) -->
      <v-expand-transition>
        <v-alert
          v-if="creationCompleted"
          :type="creationSuccess ? 'success' : 'error'"
          border="left"
          class="mt-4"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2">
              {{ creationSuccess ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <div>
              <strong>
                {{ creationSuccess ? 'Карточка успешно создана!' : 'Создание завершено с ошибками' }}
              </strong>
              <div v-if="creationStats" class="mt-1">
                <div>Всего шагов: {{ creationStats.totalSteps }}</div>
                <div>Успешно: {{ creationStats.successSteps }}</div>
                <div v-if="creationStats.failedSteps > 0">
                  С ошибками: {{ creationStats.failedSteps }}
                </div>
                <div>Общее время: {{ creationStats.totalTime }}с</div>
              </div>
            </div>
          </div>
        </v-alert>
      </v-expand-transition>
    </v-card-text>

    <!-- Футер с кнопками -->
    <v-card-actions class="pa-4 pt-0">
      <!-- Кнопка отмены -->
      <v-btn
        :disabled="isCreating && !canCancel"
        @click="cancelCreation"
        color="grey"
        text
      >
        {{ isCreating ? 'Прервать' : 'Закрыть' }}
      </v-btn>

      <v-spacer></v-spacer>

      <!-- Кнопка запуска создания -->
      <v-btn
        v-if="!isCreating && !this.creationSuccess && !creationCompleted"
        @click="startCreation"
        color="primary"
        :loading="false"
        :disabled="!canStart"
      >
        <v-icon left>mdi-play</v-icon>
        Начать создание
      </v-btn>

      <!-- Статус создания -->
      <div v-if="isCreating" class="creating-status">
        <v-progress-circular
          indeterminate
          size="20"
          width="2"
          class="mr-2"
          color="primary"
        ></v-progress-circular>
        <span class="text-caption">Выполняется...</span>
      </div>
    </v-card-actions>

    <!-- Диалог подтверждения отмены -->
    <v-dialog
      v-model="cancelConfirmDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          Подтверждение отмены
        </v-card-title>
        <v-card-text>
          Вы действительно хотите прервать создание карточки?
          <span class="error--text font-weight-medium">
              Незавершенные действия могут привести к неполным данным.
            </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="cancelConfirmDialog = false"
          >
            Продолжить
          </v-btn>
          <v-btn
            color="error"
            @click="confirmCancel"
          >
            Прервать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</v-dialog>
</template>

<script>
export default {
  name: 'ModalCreatingCard',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    // Шаги создания (из родителя)
    stepsCreating: {
      type: Array,
      default: () => []
    },
    // Функция создания карточки
    createCardFunction: {
      type: Function,
      required: true
    },
    // Можно ли отменять процесс
    canCancel: {
      type: Boolean,
      default: true
    },
    // Можно ли начинать
    canStart: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      // Локальная копия шагов
      localSteps: [],
      // Показывать ли детали ошибок
      showErrorDetails: {},
      // Диалог подтверждения отмены
      cancelConfirmDialog: false,
      // Время начала создания
      creationStartTime: null,
      // Детали текущего шага
      currentStepDetails: [],
      // Статистика создания
      creationStats: null,
      // Временные метки шагов
      stepTimers: {}
    };
  },

  computed: {
    // Двусторонняя связь для v-model
    modalVisible: {
      get() {
        return this.value;
      },
      set() {
        this.$emit('closeModal');
      }
    },

    // Текущий статус
    currentStatusText() {
      if (!this.isCreating && !this.creationCompleted) {
        return 'Готов к запуску';
      }

      const currentStep = this.localSteps.find(step => step.status === 'started');
      if (currentStep) {
        return `Выполняется: ${currentStep.text}`;
      }

      if (this.creationCompleted) {
        return this.creationSuccess ? 'Успешно завершено' : 'Завершено с ошибками';
      }

      return 'Ожидание...';
    },

    statusColorClass() {
      if (this.isCreating) return 'primary--text';
      if (this.creationCompleted) {
        return this.creationSuccess ? 'success--text' : 'error--text';
      }
      return 'grey--text';
    },

    // Общий прогресс в %
    overallProgress() {
      const totalSteps = this.localSteps.length;
      if (totalSteps === 0) return 0;

      const completedSteps = this.localSteps.filter(
        step => step.status === 'finished' || step.status === 'error'
      ).length;

      return Math.round((completedSteps / totalSteps) * 100);
    },

    // В процессе создания?
    isCreating() {
      return this.localSteps.some(step => step.status === 'started');
    },

    // Создание завершено?
    creationCompleted() {
      return this.localSteps.every(
        step => step.status === 'finished' || step.status === 'error'
      );
    },

    // Успешно ли завершено?
    creationSuccess() {
      return this.creationCompleted
        && !this.localSteps.some(step => step.status === 'error');
    },

    // Детали текущего шага
    currentStepDetail() {
      const currentStep = this.localSteps.find(step => step.status === 'started');
      if (currentStep && this.currentStepDetails.length > 0) {
        return this.currentStepDetails;
      }
      return null;
    }
  },

  watch: {
    // При изменении пропса stepsCreating обновляем локальную копию
    stepsCreating: {
      handler(newSteps) {
        this.updateLocalSteps(newSteps);
      },
      deep: true,
      immediate: true
    },

    // При открытии модалки сбрасываем состояние
    modalVisible(newVal) {
      if (newVal) {
        this.resetModal();
      }
    }
  },

  methods: {
    // Обновление локальных шагов
    updateLocalSteps(newSteps) {
      if (JSON.stringify(this.localSteps) !== JSON.stringify(newSteps)) {
        this.localSteps = JSON.parse(JSON.stringify(newSteps));

        // Обновляем таймеры для новых шагов
        this.localSteps.forEach(step => {
          if (step.status === 'started' && !step.startTime) {
            this.startStepTimer(step.id);
          } else if ((step.status === 'finished' || step.status === 'error') && step.startTime && !step.endTime) {
            this.stopStepTimer(step.id);
          }
        });
      }
    },

    // Начать создание
    async startCreation() {
      this.creationStartTime = Date.now();
      this.creationStats = null;

      // Обновляем первый шаг
      this.updateStepStatus(0, 'started');
      this.startStepTimer(0);
      this.currentStepDetails = ['Подготовка данных...'];

      try {
        await this.createCardFunction({
          // Колбэк для обновления прогресса
          onProgress: (stepId, status, details) => {
            this.handleProgressUpdate(stepId, status, details);
          },
          // Колбэк для обновления деталей шага
          onStepDetail: (details) => {
            this.currentStepDetails = Array.isArray(details) ? details : [details];
          }
        });

        // Обновляем последний шаг
        this.updateStepStatus(this.localSteps.length - 1, 'finished');
        this.stopStepTimer(this.localSteps.length - 1);

        // Собираем статистику
        this.calculateStats();
      } catch (error) {
        console.error('Ошибка создания карточки:', error);
        this.updateStepStatus(this.localSteps.length - 1, 'error', [
          error.message || 'Неизвестная ошибка'
        ]);
        this.stopStepTimer(this.localSteps.length - 1);
        this.calculateStats();
      }
    },

    // Обработка обновления прогресса
    handleProgressUpdate(stepId, status, details = []) {
      // Останавливаем предыдущий шаг
      const previousStep = this.localSteps.find(step => step.status === 'started');
      if (previousStep && previousStep.id !== stepId) {
        this.updateStepStatus(previousStep.id, 'finished');
        this.stopStepTimer(previousStep.id);
      }

      // Обновляем текущий шаг
      this.updateStepStatus(stepId, status, details);

      if (status === 'started') {
        this.startStepTimer(stepId);
      } else if (status === 'finished' || status === 'error') {
        this.stopStepTimer(stepId);
      }

      // Если это последний шаг, собираем статистику
      if (stepId === this.localSteps.length - 1
        && (status === 'finished' || status === 'error')) {
        this.calculateStats();
      }
    },

    // Обновить статус шага
    updateStepStatus(stepId, status, detailError = []) {
      const stepIndex = this.localSteps.findIndex(step => step.id === stepId);
      if (stepIndex !== -1) {
        const step = this.localSteps[stepIndex];

        // Если статус меняется на started - записываем время начала
        if (status === 'started' && step.status !== 'started') {
          step.startTime = Date.now();
          step.duration = null;
        }

        // Если статус меняется на finished или error - записываем время окончания
        if ((status === 'finished' || status === 'error')
          && step.status === 'started' && step.startTime) {
          step.endTime = Date.now();
          step.duration = Math.round((step.endTime - step.startTime) / 1000);
        }

        step.status = status;

        if (detailError.length > 0) {
          step.detailError = Array.isArray(detailError) ? detailError : [detailError];
        }

        // Обновляем массив для реактивности
        this.$set(this.localSteps, stepIndex, { ...step });
      }
    },

    // Начать таймер шага
    startStepTimer(stepId) {
      if (!this.stepTimers[stepId]) {
        this.stepTimers[stepId] = Date.now();
      }
    },

    // Остановить таймер шага
    stopStepTimer(stepId) {
      if (this.stepTimers[stepId]) {
        const duration = Date.now() - this.stepTimers[stepId];
        delete this.stepTimers[stepId];
        return Math.round(duration / 1000);
      }
      return 0;
    },

    // Рассчитать статистику
    calculateStats() {
      const finishedSteps = this.localSteps.filter(
        step => step.status === 'finished'
      ).length;

      const errorSteps = this.localSteps.filter(
        step => step.status === 'error'
      ).length;

      const totalTime = this.creationStartTime
        ? Math.round((Date.now() - this.creationStartTime) / 1000) : 0;

      this.creationStats = {
        totalSteps: this.localSteps.length,
        successSteps: finishedSteps,
        failedSteps: errorSteps,
        totalTime
      };
    },

    // Отмена создания
    cancelCreation() {
      if (this.isCreating) {
        this.cancelConfirmDialog = true;
      } else {
        this.closeModal();
      }
    },

    // Подтверждение отмены
    confirmCancel() {
      this.cancelConfirmDialog = false;
      this.closeModal();
      this.$emit('cancel-creation');
    },

    // Повторить шаг
    retryStep(step) {
      this.$emit('retry-step', step.id);
    },

    // Повторить ошибочные шаги
    retryFailedSteps() {
      const failedSteps = this.localSteps
        .filter(step => step.status === 'error')
        .map(step => step.id);

      this.$emit('retry-steps', failedSteps);
    },

    // Закрыть модалку
    closeModal() {
      this.modalVisible = false;
    },

    // Сброс состояния модалки
    resetModal() {
      this.localSteps = JSON.parse(JSON.stringify(this.stepsCreating));
      this.showErrorDetails = {};
      this.currentStepDetails = [];
      this.creationStats = null;
      this.creationStartTime = null;
      this.stepTimers = {};
    },

    // Форматирование времени
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    // Класс для шага
    getStepClass(step) {
      const classes = [];

      switch (step.status) {
        case 'finished':
          classes.push('step-finished');
          break;
        case 'error':
          classes.push('step-error');
          break;
        case 'started':
          classes.push('step-started');
          break;
        default:
          classes.push('step-pending');
      }

      return classes.join(' ');
    },

    // Переключить детали ошибки
    toggleErrorDetails(stepId) {
      this.$set(this.showErrorDetails, stepId, !this.showErrorDetails[stepId]);
    }
  }
};
</script>

<style scoped>
.create-card-modal {
  border-radius: 8px;
  overflow: hidden;
}

.steps-list {
  background: transparent;
}

.step-item {
  height: 100%;
  border-radius: 8px;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  -webkit-box-shadow: 0px 12px 17px 3px rgba(13, 115, 193, 0.2);
  -moz-box-shadow: 0px 12px 17px 3px rgba(13, 115, 193, 0.2);
  box-shadow: 0px 12px 17px 3px rgba(13, 115, 193, 0.2);

}

.step-item.step-finished {
  border-left-color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

.step-item.step-error {
  border-left-color: #f44336;
  background-color: rgba(244, 67, 54, 0.05);
}

.step-item.step-started {
  border-left-color: #2196f3;
  background-color: rgba(33, 150, 243, 0.05);
  animation: pulse 2s infinite;
}

.step-item.step-pending {
  border-left-color: #9e9e9e;
}

.step-avatar {
  min-width: 40px !important;
}

.step-icon {
  font-size: 24px;
}

.step-title {
  font-weight: 500;
  font-size: 0.95rem;
}

.creating-status {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.2);
  }
  70% {
    box-shadow: 0 0 0 5px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}
</style>
